import { Component, EventEmitter, Input, Output, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';

import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';
import { Url } from '../../../../../models/user.model';

// Interface for a URL entry
export interface UrlRec {
    id: string; // Unique ID for this URL entry
    recId?: number;
    url: string;
    description: string;
    isActive: number;
    isApproved: number;
    UrlRecID: number;
}

@Component({
    selector: 'app-urls',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        InputTextarea,
        ButtonModule,
        ToastModule,
        TableModule,
        TagModule
    ],
    templateUrl: './urls.component.html',
    styleUrls: ['./urls.component.scss'],
    providers: [MessageService]
})
export class UrlsComponent implements OnInit {
    @Input() urls: UrlRec[] = [];
    @Output() urlsChange = new EventEmitter<UrlRec[]>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() prevStep = new EventEmitter<void>();

    @Output() skipToLastStep = new EventEmitter<void>();


    @Input() customerRecId!: number;

    readonly DESCRIPTION_MAX_LENGTH = 250;

    isDialogMode = false;
    isCreate = false;

    newEntry: UrlRec = {
        id: '',
        url: '',
        description: '',
        UrlRecID: 0,
        isActive: 1,
        isApproved: 1
    };

    constructor(public translateServ: TranslateService,
        private messageService: MessageService,
        public dirServ: DirectionService,
        private viewScopeService: ViewScopeService,
        @Optional() public ref: DynamicDialogRef,
        @Optional() public config: DynamicDialogConfig,
        @Optional() private maintainAccountService: MaintainAccountService
    ) { }

    ngOnInit(): void {
        if (this.config && this.config.data) {
            this.isDialogMode = this.config.data.isDialog;
            this.customerRecId = this.config.data.customerRecId;
            this.isCreate = this.config.data.isCreate;

            if (!this.isCreate && this.config.data.nameEntry) {
                // Edit mode: initialize with existing record(s)
                this.urls = [this.config.data.nameEntry];
            } else {
                // Create mode: do NOT pre-push a row here.
                // The footer row with newEntry will handle input.
                this.urls = [];
            }
        }
        else {
            this.isDialogMode = false;
            this.isCreate = true;

            // Ensure all existing items have a unique ID for tracking.
            this.urls.forEach(item => {
                if (!item.id) {
                    item.id = this.generateUniqueId();
                }
            });
            this.resetNewEntry();
        }
    }

    private generateUniqueId(): string {
        return 'url_' + Math.random().toString(36).substring(2, 9);
    }

    resetNewEntry(): void {
        this.newEntry = {
            id: this.generateUniqueId(),
            url: '',
            description: '',
            UrlRecID: 0,
            isActive: 1,
            isApproved: 1
        };
    }

    addEntry(): void {
        if (!this.newEntry.url.trim()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'URL is required.',
                life: 3000
            });
            return;
        }
        // Basic URL validation regex (can be made more robust if needed)
        const urlRegex = /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|[a-zA-Z0-9]+\.[^\s]{2,})$/i;
        if (!urlRegex.test(this.newEntry.url.trim())) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please enter a valid URL (e.g., https://example.com).',
                life: 3000
            });
            return;
        }
        if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }

        this.urls.push({ ...this.newEntry });
        this.emitChanges();
        if (this.isCreate) {
            this.resetNewEntry();
        }
        this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: 'New URL added.',
            life: 1500
        });
    }

    removeEntry(entryToRemove: UrlRec): void {
        this.urls = this.urls.filter(entry => entry.id !== entryToRemove.id);
        this.emitChanges();
        this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: 'URL removed.',
            life: 1500
        });
    }

    emitChanges(): void {
        this.urlsChange.emit(this.urls);
    }

    getRemainingChars(description: string): number {
        return this.DESCRIPTION_MAX_LENGTH - description.length;
    }

    validateAndProceed(): void {
        let isValid = true;
        for (const entry of this.urls) {
            if (!entry.url.trim()) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `URL is required for entry "${entry.id}".`,
                    life: 3000
                });
                break;
            }
            const urlRegex = /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|[a-zA-Z0-9]+\.[^\s]{2,})$/i;
            if (!urlRegex.test(entry.url.trim())) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Please enter a valid URL for entry "${entry.id}".`,
                    life: 3000
                });
                break;
            }
            if ((entry.description?.length ?? 0) > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for URL "${entry.url}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (isValid) {
            this.nextStep.emit();
            this.messageService.clear();
        }
    }

    goToPreviousStep(): void {
        this.prevStep.emit();
    }

    applyRowChanges(entry: UrlRec): void {
        // Simply emit the changes so parent knows data is dirty
        this.emitChanges();

        this.messageService.add({
            severity: 'info',
            summary: 'Changes Applied',
            detail: `Changes applied for "${entry.url}". Remember to press Save to persist.`,
            life: 2500
        });
    }


    /**
     * This function is intended for the dialog's "save" button.
     * It validates the form and then calls the API to save/update the data.
     * It uses forkJoin to wait for all API calls to complete before closing the dialog.
     */
    saveAndSubmit(): void {
        // First, run validation
        let isValid = true;

        for (const entry of this.urls) {
            if (!entry.url.trim()) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Name for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if (entry.description.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.url}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (!isValid) return;

        // Only attempt API calls if the service is available
        if (this.maintainAccountService) {
            const requests = this.urls.map(entry => {
                const basePayload: any = {
                    Url: entry.url,
                    Descr: entry.description ?? '',   // safe for null
                    IsActive: entry.isActive,
                    IsApproved: entry.isApproved,
                    UserRecID: this.viewScopeService.getCurrentOpertaingUserId()
                };

                if (this.isCreate) {
                    basePayload.RecID = this.customerRecId;
                } else {
                    basePayload.RecID = entry.recId;
                    basePayload.UrlRecID = entry.UrlRecID;
                }

                return basePayload;
            });

            console.log("Data we will save:", requests);

            requests.forEach(record => {
                this.maintainAccountService
                    .insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1), JSON.stringify(record))
                    .subscribe({
                        next: (response) => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Saved record for "${record.Url}".`,
                                life: 2000
                            });

                            if (this.ref && record === requests[requests.length - 1]) {
                                this.ref.close(response);
                            }
                        },
                        error: (error) => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: `Failed to save record for "${record.Url}".`,
                                life: 3000
                            });
                        },
                        complete: () => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Complete',
                                detail: 'All records processed.',
                                life: 2000
                            });

                            if (this.ref) {
                                this.ref.close(true);
                            }
                        }
                    });
            });


        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'This functionality is not available in standalone mode.',
                life: 3000
            });
        }
    }


    getSeverity(val: boolean): string {
        return val ? 'success' : 'danger';
    }

    toggleIsActive(entry: any): void {
        entry.isActive = entry.isActive === 1 ? 0 : 1;
    }

    toggleIsApproved(entry: any): void {
        entry.isApproved = entry.isApproved === 1 ? 0 : 1;
    }


    closeDialog(shouldReload: boolean = false): void {
        if (this.ref) {
            this.ref.close(shouldReload);
        }
    }

}
